import { lookup } from "@repo/vihangam-yog-shared";
import { ExternalLink, MessageSquare, Paperclip, Send, X } from "lucide-react";
import type { CSSProperties } from "react";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { apiUrl, authHeaders, useApiAccess } from "../api";
import { useAuth } from "../auth/AuthContext";
import { CHAPTER } from "../chapter";
import { friendlyError } from "../lib/friendlyError";
import { NAV_LABEL } from "../lib/labels";
import { trpc } from "../trpc";

import { relativeTime, StatusPanel } from "./feedback/StatusPanel";

type Status = "new" | "in_progress" | "live" | "declined";
type Filter = "open" | "live" | "declined" | "all";
type Area = "website" | "admin";

/** One uploaded photo or video, as `/feedback/upload` answered. */
interface Attachment {
  key: string;
  url: string;
  name: string;
  type: string;
  size: number;
}

/** Input to adminCreateFeedback. */
interface CreateInput {
  area: Area;
  page: string;
  body: string;
  name?: string;
  attachments?: Attachment[];
}

/** Input to adminUpdateFeedback. Words go in the thread, not here. */
interface ResolveInput {
  id: string;
  status: Status;
  liveUrl?: string;
  name?: string;
}

/** Input to adminReplyFeedback. */
interface ReplyInput {
  id: string;
  body: string;
  name?: string;
  attachments?: Attachment[];
}

/** One reply in an item's thread, as adminListFeedback returns it. */
interface Reply {
  id: string;
  authorKind: string;
  authorName: string | null;
  authorEmail: string | null;
  body: string;
  attachments: Attachment[];
  createdAt: string;
}

/**
 * One row as returned by adminListFeedback. `response` mirrors the wire
 * type only: the thread replaced it and nothing here reads it.
 */
interface FeedbackItem {
  id: string;
  authorEmail: string;
  authorName: string | null;
  area: string;
  page: string | null;
  body: string;
  attachments: Attachment[];
  status: string;
  response: string | null;
  liveUrl: string | null;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  notifiedAt: string | null;
  replies: Reply[];
  isMine: boolean;
}

/** Status names people read. The stored values stay new/in_progress/live/declined. */
const STATUS_LABEL = {
  new: "New",
  in_progress: "Being worked on",
  live: "Done",
  declined: "Not doing",
} satisfies Record<Status, string>;

const STATUSES: Status[] = ["new", "in_progress", "live", "declined"];

const AREA_LABEL = {
  website: "Website",
  admin: "Team app",
} satisfies Record<Area, string>;

const AREAS: Area[] = ["website", "admin"];

const FILTERS: { id: Filter; label: string }[] = [
  { id: "open", label: "To do" },
  { id: "live", label: "Done" },
  { id: "declined", label: "Not doing" },
  { id: "all", label: "Everything" },
];

/** What an empty filter says, once something has been posted at all. */
const EMPTY_MESSAGE = {
  open: "Nothing waiting. Everything posted so far has been dealt with.",
  live: "Nothing marked done yet.",
  declined: "Nothing turned down.",
  all: "Nothing posted yet.",
} satisfies Record<Filter, string>;

const PAGES_BY_AREA = {
  website: [
    "Home",
    "Events",
    "Register",
    "Thanks page",
    "Gallery",
    "Activities",
    "Inspiration",
    "Resources",
    "Swarved",
    "Calendar",
    "Contact",
    "Other",
  ],
  // The pages this chapter's team app actually has, named as the sidebar
  // names them, so a page the chapter doesn't run is never offered.
  admin: [...CHAPTER.features.map((feature) => NAV_LABEL[feature]), "Other"],
} satisfies Record<Area, readonly string[]>;

const MAX_ATTACHMENTS = 5;
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
const MAX_BODY = 2000;
/** The character count only appears this close to the limit. */
const COUNT_FROM = 1800;
const ACCEPT =
  "image/png,image/jpeg,image/webp,image/gif,image/heic,video/mp4,video/quicktime,video/webm";

/**
 * Upload refusals whose text is a sentence written for people (the file
 * is too big, or not a photo or video). Anything else the route answers
 * is setup or protocol talk and gets the plain line instead.
 */
const PLAIN_UPLOAD_REFUSALS = new Set([413, 415]);

function matchesFilter(item: FeedbackItem, filter: Filter): boolean {
  if (filter === "all") {
    return true;
  }
  if (filter === "open") {
    return item.status === "new" || item.status === "in_progress";
  }
  return item.status === filter;
}

const DATE_TIME = {
  day: "numeric",
  month: "short",
  hour: "numeric",
  minute: "2-digit",
} satisfies Intl.DateTimeFormatOptions;

const DATE_TIME_WITH_YEAR = {
  ...DATE_TIME,
  year: "numeric",
} satisfies Intl.DateTimeFormatOptions;

/** "9 Sept, 3:40 pm", with the year only when it isn't this year. */
function formatDate(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleString(
    "en-AU",
    date.getFullYear() === new Date().getFullYear()
      ? DATE_TIME
      : DATE_TIME_WITH_YEAR,
  );
}

// oxlint-disable-next-line anti-slop/no-unknown-parameters -- type predicate: this IS the parse boundary for the upload route's JSON answer
function isAttachment(value: unknown): value is Attachment {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  return (
    "key" in value &&
    typeof value.key === "string" &&
    "url" in value &&
    typeof value.url === "string" &&
    "name" in value &&
    typeof value.name === "string" &&
    "type" in value &&
    typeof value.type === "string" &&
    "size" in value &&
    typeof value.size === "number"
  );
}

/** Text inputs, selects and textareas: 16px so iOS doesn't zoom, 44px tall. */
const FIELD_CLASS =
  "min-h-11 w-full rounded-lg border border-[color:var(--color-line-strong)] bg-[color:var(--color-paper)] px-3.5 py-2.5 text-[16px] leading-[1.5] font-normal text-[color:var(--color-ink)] outline-none placeholder:text-[color:var(--color-ink-faint)] focus:border-[color:var(--color-amber)]";

/** The main action in a row, sized for a thumb at the venue. */
const PRIMARY_BUTTON =
  "inline-flex min-h-11 items-center gap-2 rounded-full bg-[color:var(--color-amber)] px-5 text-[15px] font-semibold text-[color:var(--color-paper)] transition-opacity hover:opacity-90 disabled:opacity-50";

const SECONDARY_BUTTON =
  "inline-flex min-h-11 items-center gap-2 rounded-full border border-[color:var(--color-line-strong)] px-5 text-[15px] font-semibold text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink)] disabled:opacity-50";

/** A pill that wraps a visually hidden native input, with the focus ring the input can't show. */
const INPUT_PILL =
  "inline-flex min-h-11 items-center gap-2 rounded-full border border-[color:var(--color-line-strong)] px-5 text-[15px] font-semibold text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink)] has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-[color:var(--color-amber)]";

const ERROR_LINE = "text-[14px] text-[color:var(--color-danger)]";

/**
 * Feedback: the organising team's board for site and admin changes.
 * Anyone signed in can post, with photos or a short video. The
 * assistant reads the board through the service API, makes the
 * change, marks the item Done with a link (which emails the author),
 * and keeps a short note of what it is doing under the form.
 * Superadmins can move items along by hand too.
 *
 * Auto-refreshes every 30s while focused.
 */
export function Feedback() {
  const { role, setup } = useAuth();
  const query = trpc.adminListFeedback.useQuery(undefined, {
    retry: false,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
  });
  const [filter, setFilter] = useState<Filter>("open");

  const all = useMemo<FeedbackItem[]>(() => query.data ?? [], [query.data]);
  const visible = useMemo(
    () => all.filter((i) => matchesFilter(i, filter)),
    [all, filter],
  );

  return (
    <section className="mx-auto max-w-[1100px] px-5 py-10 md:px-10 md:py-12">
      <header className="border-b border-[color:var(--color-line)] pb-6">
        <p className="eyebrow">Organising team</p>
        <h1 className="page-title mt-2">Feedback</h1>
        <p className="mt-3 max-w-2xl text-[16px] leading-[1.6] text-[color:var(--color-ink-soft)]">
          Write down anything you&apos;d change on the website or in this team
          app. Adi and Claude, the AI assistant he works with, read this board
          every few minutes, make the change and mark it Done here with a link.
        </p>
        <p className="mt-2 max-w-2xl text-[16px] leading-[1.6] text-[color:var(--color-ink-soft)]">
          Replies marked Claude are written by the assistant Adi works with.{" "}
          {setup.email
            ? "You'll get an email when your change is done."
            : "Check back here to see progress."}
        </p>
      </header>

      <FeedbackForm />

      <StatusPanel />

      {query.isLoading ? (
        <p
          role="status"
          className="mt-8 text-[16px] text-[color:var(--color-ink-soft)]"
        >
          Loading the feedback…
        </p>
      ) : null}

      {query.error ? (
        <div
          role="alert"
          className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-2"
        >
          <p className={ERROR_LINE}>
            {query.data
              ? "Couldn't refresh the feedback, so what's below may be out of date."
              : "Couldn't load the feedback."}{" "}
            Check your internet connection, then tap Try again.
          </p>
          <button
            type="button"
            onClick={() => void query.refetch()}
            className={SECONDARY_BUTTON}
          >
            Try again
          </button>
        </div>
      ) : null}

      {query.data ? (
        <div className="mt-10">
          <h2 className="sr-only">Posted feedback</h2>
          <div role="group" aria-label="Show" className="flex flex-wrap gap-2">
            {FILTERS.map((f) => {
              const isActive = filter === f.id;
              const count = all.filter((i) => matchesFilter(i, f.id)).length;
              return (
                <button
                  key={f.id}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setFilter(f.id)}
                  className={`inline-flex min-h-11 items-center gap-1.5 rounded-full px-4 text-[14px] font-semibold transition-colors ${
                    isActive
                      ? "bg-[color:var(--color-amber)] text-[color:var(--color-paper)]"
                      : "border border-[color:var(--color-line-strong)] text-[color:var(--color-ink)] hover:border-[color:var(--color-ink)]"
                  }`}
                >
                  {f.label}{" "}
                  <span
                    className={
                      isActive
                        ? "font-normal tabular-nums"
                        : "font-normal text-[color:var(--color-ink-soft)] tabular-nums"
                    }
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mt-6 flex flex-col gap-4">
            {visible.length === 0 ? (
              <p className="empty-state">
                {all.length === 0
                  ? "Nothing posted yet."
                  : EMPTY_MESSAGE[filter]}
              </p>
            ) : (
              visible.map((item) => (
                <FeedbackCard
                  key={item.id}
                  item={item}
                  canResolve={role === "superadmin"}
                />
              ))
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}

/** A file waiting to be uploaded, with a preview url to show it. */
interface PendingFile {
  file: File;
  previewUrl: string;
}

type UploadState =
  | { kind: "idle" }
  | { kind: "uploading"; index: number; total: number }
  | { kind: "error"; message: string };

/** What `usePendingAttachments` hands its callers. */
interface PendingAttachments {
  pending: PendingFile[];
  upload: UploadState;
  /** Add chosen files, up to the limit; too-big files are refused with a message. */
  addFiles: (list: FileList | null) => void;
  removePending: (target: PendingFile) => void;
  /** Drop everything chosen (after a successful send, or on cancel). */
  clear: () => void;
  /**
   * Upload every pending file in turn through `/feedback/upload`. Answers
   * the stored attachments, or null when one failed (the message is in
   * `upload`).
   */
  uploadAll: () => Promise<Attachment[] | null>;
}

/**
 * Files chosen but not yet uploaded, and the upload itself. The create
 * form and the reply box share this so the limits, the error copy and
 * the upload-then-mutate flow cannot drift between them. Preview urls
 * are browser resources; they are revoked when removed, cleared, or
 * when the owner unmounts.
 */
function usePendingAttachments(): PendingAttachments {
  const access = useApiAccess();
  const [pending, setPending] = useState<PendingFile[]>([]);
  const [upload, setUpload] = useState<UploadState>({ kind: "idle" });

  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  useEffect(
    () => () =>
      pendingRef.current.forEach((p) => URL.revokeObjectURL(p.previewUrl)),
    [],
  );

  function addFiles(list: FileList | null) {
    if (!list) {
      return;
    }
    const picked = Array.from(list);
    const chosen = picked.slice(
      0,
      Math.max(0, MAX_ATTACHMENTS - pending.length),
    );
    const tooBig = chosen.find((f) => f.size > MAX_UPLOAD_BYTES);
    if (tooBig) {
      setUpload({
        kind: "error",
        message: `${tooBig.name} is over 25 MB. Trim the video or pick a smaller photo.`,
      });
      return;
    }
    const dropped = picked.length - chosen.length;
    setUpload(
      dropped > 0
        ? {
            kind: "error",
            message: `Only ${MAX_ATTACHMENTS} files fit, so ${dropped} ${dropped === 1 ? "was" : "were"} left out.`,
          }
        : { kind: "idle" },
    );
    setPending((current) => [
      ...current,
      ...chosen.map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file),
      })),
    ]);
  }

  function removePending(target: PendingFile) {
    URL.revokeObjectURL(target.previewUrl);
    setPending((current) => current.filter((p) => p !== target));
  }

  function clear() {
    setPending((current) => {
      current.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      return [];
    });
    setUpload({ kind: "idle" });
  }

  async function uploadAll(): Promise<Attachment[] | null> {
    let headers: HeadersInit;
    try {
      headers = await authHeaders(access);
    } catch {
      setUpload({
        kind: "error",
        message:
          "The photos didn't upload. Check your internet connection and try again.",
      });
      return null;
    }
    const done: Attachment[] = [];
    for (const [index, item] of pending.entries()) {
      setUpload({ kind: "uploading", index: index + 1, total: pending.length });
      const form = new FormData();
      form.set("file", item.file);
      let response: Response;
      try {
        response = await fetch(apiUrl("/feedback/upload"), {
          method: "POST",
          headers,
          body: form,
        });
      } catch {
        setUpload({
          kind: "error",
          message: `${item.file.name} didn't upload. Check your internet connection and try again.`,
        });
        return null;
      }
      if (!response.ok) {
        const reason = PLAIN_UPLOAD_REFUSALS.has(response.status)
          ? (await response.text().catch(() => "")).trim()
          : "";
        setUpload({
          kind: "error",
          message: `${item.file.name} didn't upload. ${reason || "Please try again, or pick a smaller file."}`,
        });
        return null;
      }
      const answer: unknown = await response.json().catch(() => null);
      if (!isAttachment(answer)) {
        setUpload({
          kind: "error",
          message: `${item.file.name} didn't upload. Please try again, or pick a smaller file.`,
        });
        return null;
      }
      done.push(answer);
    }
    setUpload({ kind: "idle" });
    return done;
  }

  return { pending, upload, addFiles, removePending, clear, uploadAll };
}

/**
 * The file button, the limits line and the thumbnails of what is
 * waiting to go up. The same picker sits in the create form and in
 * every reply box.
 */
function AttachmentPicker({
  files,
  busy,
  className,
}: {
  files: PendingAttachments;
  busy: boolean;
  className: string;
}) {
  const { pending } = files;
  const limitsId = useId();
  const full = pending.length >= MAX_ATTACHMENTS;
  return (
    <div className={`${className} flex flex-col gap-2`}>
      <p className="field-label">Photos or a short video (optional)</p>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <label
          className={`${INPUT_PILL} has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50`}
        >
          <Paperclip size={16} aria-hidden="true" />
          {full
            ? `${MAX_ATTACHMENTS} files added`
            : pending.length === 0
              ? "Attach files"
              : "Add more"}
          <input
            type="file"
            multiple
            accept={ACCEPT}
            aria-describedby={limitsId}
            disabled={busy || full}
            onChange={(e) => {
              files.addFiles(e.target.files);
              // Reset so choosing the same file again fires change.
              e.target.value = "";
            }}
            className="sr-only"
          />
        </label>
        <span
          id={limitsId}
          className="text-[14px] text-[color:var(--color-ink-soft)]"
        >
          Up to {MAX_ATTACHMENTS} files, 25 MB each.
        </span>
      </div>
      {pending.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {pending.map((p) => (
            <li
              key={p.previewUrl}
              className="relative h-20 w-20 overflow-hidden rounded-lg border border-[color:var(--color-line)] bg-[color:var(--color-bg)]"
            >
              {p.file.type.startsWith("video/") ? (
                <video
                  src={p.previewUrl}
                  muted
                  className="h-full w-full object-cover"
                />
              ) : (
                <img
                  src={p.previewUrl}
                  alt={p.file.name}
                  className="h-full w-full object-cover"
                />
              )}
              {/* A 44px target in the corner, drawn as a smaller circle. */}
              <button
                type="button"
                aria-label={`Remove ${p.file.name}`}
                disabled={busy}
                onClick={() => files.removePending(p)}
                className="absolute top-0 right-0 flex h-11 w-11 items-start justify-end p-1 disabled:opacity-50"
              >
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[color:var(--color-paper)]/90 text-[color:var(--color-ink)] shadow">
                  <X size={16} aria-hidden="true" />
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function FeedbackForm() {
  const { user, setup } = useAuth();
  const utils = trpc.useUtils();
  const formId = useId();
  const [area, setArea] = useState<Area>("website");
  const [page, setPage] = useState<string>("Home");
  const [where, setWhere] = useState("");
  const [body, setBody] = useState("");
  const files = usePendingAttachments();
  const [posted, setPosted] = useState(false);

  const create = trpc.adminCreateFeedback.useMutation({
    onSuccess: () => {
      setBody("");
      setWhere("");
      files.clear();
      setPosted(true);
      void utils.adminListFeedback.invalidate();
    },
  });

  const pages = PAGES_BY_AREA[area];
  const trimmed = body.trim();
  const pageValue = where.trim() ? `${page}: ${where.trim()}` : page;
  const upload = files.upload;
  const busy = create.isPending || upload.kind === "uploading";
  const nearLimit = body.length > COUNT_FROM;
  const errorMessage =
    upload.kind === "error"
      ? upload.message
      : create.error
        ? friendlyError(
            create.error,
            "That didn't post. Check your internet connection and try again.",
          )
        : null;

  function chooseArea(next: Area) {
    setArea(next);
    setPage(PAGES_BY_AREA[next][0] ?? "Other");
  }

  return (
    <form
      aria-labelledby={`${formId}-title`}
      className="surface mt-8 p-5 md:p-6"
      onSubmit={(e) => {
        e.preventDefault();
        if (trimmed.length === 0 || busy) {
          return;
        }
        setPosted(false);
        void (async () => {
          const attachments = await files.uploadAll();
          if (attachments === null) {
            return;
          }
          const input: CreateInput = { area, page: pageValue, body: trimmed };
          if (user?.fullName) {
            input.name = user.fullName;
          }
          if (attachments.length > 0) {
            input.attachments = attachments;
          }
          create.mutate(input);
        })();
      }}
    >
      <h2
        id={`${formId}-title`}
        className="text-[20px] leading-[1.3] font-semibold text-[color:var(--color-ink)]"
      >
        Suggest a change
      </h2>

      <fieldset className="mt-5">
        <legend className="field-label">
          Is it about the website or this team app?
        </legend>
        <div className="mt-2 flex flex-wrap gap-2">
          {AREAS.map((a) => (
            <label
              key={a}
              className={`${INPUT_PILL} has-[:checked]:border-[color:var(--color-ink)] has-[:checked]:bg-[color:var(--color-ink)] has-[:checked]:text-[color:var(--color-paper)]`}
            >
              <input
                type="radio"
                name={`${formId}-area`}
                value={a}
                checked={area === a}
                onChange={() => chooseArea(a)}
                className="sr-only"
              />
              {AREA_LABEL[a]}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="mt-5 grid gap-4 md:grid-cols-[240px_1fr]">
        <div className="field-label">
          <label htmlFor={`${formId}-page`}>Which page?</label>
          <select
            id={`${formId}-page`}
            value={page}
            onChange={(e) => {
              const next = e.target.value;
              setPage(pages.find((p) => p === next) ?? "Other");
            }}
            className={FIELD_CLASS}
          >
            {pages.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <div className="field-label">
          <label htmlFor={`${formId}-where`}>
            Where on the page (optional)
          </label>
          <input
            id={`${formId}-where`}
            type="text"
            value={where}
            onChange={(e) => {
              setWhere(e.target.value);
              setPosted(false);
            }}
            maxLength={160}
            placeholder="For example: the photo at the top, or paste a link"
            className={FIELD_CLASS}
          />
        </div>
      </div>

      <div className="field-label mt-5">
        <label htmlFor={`${formId}-body`}>What you&apos;d like changed</label>
        <textarea
          id={`${formId}-body`}
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            setPosted(false);
          }}
          rows={4}
          maxLength={MAX_BODY}
          aria-describedby={nearLimit ? `${formId}-count` : undefined}
          placeholder="What you see, and what you'd like instead."
          className={`${FIELD_CLASS} resize-y`}
        />
        {nearLimit ? (
          <span
            id={`${formId}-count`}
            className="text-[14px] font-normal text-[color:var(--color-ink-soft)] tabular-nums"
          >
            {MAX_BODY - body.length} characters left
          </span>
        ) : null}
      </div>

      <AttachmentPicker files={files} busy={busy} className="mt-5" />

      <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2">
        <button
          type="submit"
          disabled={busy || trimmed.length === 0}
          className={PRIMARY_BUTTON}
        >
          <Send size={16} aria-hidden="true" />
          {upload.kind === "uploading"
            ? `Uploading ${upload.index} of ${upload.total}…`
            : create.isPending
              ? "Posting…"
              : "Post feedback"}
        </button>
        <p
          role="status"
          className="text-[14px] text-[color:var(--color-ink-soft)]"
        >
          {posted
            ? setup.email
              ? "Posted. You'll get an email when it's done."
              : "Posted. It's on the board below."
            : null}
        </p>
      </div>
      {errorMessage ? (
        <p role="alert" className={`mt-3 ${ERROR_LINE}`}>
          {errorMessage}
        </p>
      ) : null}
    </form>
  );
}

function StatusChip({ status }: { status: string }) {
  const tone =
    status === "new"
      ? {
          bg: "color-mix(in srgb, var(--color-amber) 15%, transparent)",
          color: "var(--color-amber)",
        }
      : status === "in_progress"
        ? {
            bg: "color-mix(in srgb, var(--color-ink) 10%, transparent)",
            color: "var(--color-ink)",
          }
        : status === "live"
          ? {
              bg: "color-mix(in srgb, var(--color-success) 14%, transparent)",
              color: "var(--color-success)",
            }
          : {
              bg: "transparent",
              color: "var(--color-ink-soft)",
            };
  const style: CSSProperties = { background: tone.bg, color: tone.color };
  if (status === "declined") {
    style.border = "1px solid var(--color-line-strong)";
  }
  return (
    <span className="badge shrink-0 px-2.5 py-1" style={style}>
      {lookup(STATUS_LABEL, status) ?? status}
    </span>
  );
}

/**
 * Stored attachments on an item or a reply: image thumbnails that open
 * full size, videos with controls. `className` sets the gap above.
 */
function Attachments({
  items,
  className = "mt-4",
}: {
  items: Attachment[];
  className?: string;
}) {
  if (items.length === 0) {
    return null;
  }
  return (
    <ul className={`${className} flex flex-wrap gap-2`}>
      {items.map((a) => (
        <li key={a.key}>
          {a.type.startsWith("video/") ? (
            <video
              controls
              preload="metadata"
              src={apiUrl(a.url)}
              className="max-h-48 max-w-full rounded-lg border border-[color:var(--color-line)] bg-black"
            >
              <track kind="captions" />
            </video>
          ) : (
            <a
              href={apiUrl(a.url)}
              target="_blank"
              rel="noreferrer"
              title={a.name}
              className="block h-24 w-24 overflow-hidden rounded-lg border border-[color:var(--color-line)] bg-[color:var(--color-bg)]"
            >
              <img
                src={apiUrl(a.url)}
                alt={a.name}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </a>
          )}
        </li>
      ))}
    </ul>
  );
}

function FeedbackCard({
  item,
  canResolve,
}: {
  item: FeedbackItem;
  canResolve: boolean;
}) {
  const areaLabel = lookup(AREA_LABEL, item.area) ?? item.area;
  return (
    <article className="surface p-5 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        <p className="min-w-0 text-[14px] leading-[1.5] text-[color:var(--color-ink-soft)]">
          <span className="font-semibold text-[color:var(--color-ink)]">
            {item.authorName ?? item.authorEmail}
          </span>
          {item.isMine ? (
            <>
              {" "}
              <span className="badge border border-[color:var(--color-line-strong)] text-[color:var(--color-ink-soft)]">
                Your post
              </span>
            </>
          ) : null}
          {" · "}
          {item.page ? `${areaLabel} › ${item.page}` : areaLabel}
          {" · "}
          <time dateTime={item.createdAt} title={formatDate(item.createdAt)}>
            {relativeTime(item.createdAt)}
          </time>
        </p>
        <StatusChip status={item.status} />
      </div>

      <p className="mt-3 text-[16px] leading-[1.65] whitespace-pre-wrap text-[color:var(--color-ink)] [overflow-wrap:anywhere]">
        {item.body}
      </p>

      <Attachments items={item.attachments} />

      {item.liveUrl ? (
        <a
          href={item.liveUrl}
          target="_blank"
          rel="noreferrer"
          className={`mt-4 ${SECONDARY_BUTTON}`}
        >
          <ExternalLink size={16} aria-hidden="true" />
          See the change
        </a>
      ) : null}

      <Thread item={item} />

      {canResolve ? (
        <ResolveControls key={`${item.id}-${item.updatedAt}`} item={item} />
      ) : null}
    </article>
  );
}

const CLAUDE_LABEL = "Claude, with Adi";

/** The small round mark that sits beside every reply written by Claude. */
function ClaudeMark() {
  return (
    <span
      aria-hidden="true"
      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[color:var(--color-ink)] text-[13px] leading-none font-semibold text-[color:var(--color-paper)]"
    >
      C
    </span>
  );
}

function ReplyLine({ reply }: { reply: Reply }) {
  const isClaude = reply.authorKind === "claude";
  return (
    <li className="flex gap-3">
      {isClaude ? (
        <ClaudeMark />
      ) : (
        <span
          aria-hidden="true"
          className="flex h-6 w-6 shrink-0 items-center justify-center"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--color-ink-faint)]" />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-baseline gap-x-2 text-[13px] leading-6 text-[color:var(--color-ink-soft)]">
          <span className="font-semibold text-[color:var(--color-ink)]">
            {isClaude
              ? CLAUDE_LABEL
              : (reply.authorName ?? reply.authorEmail ?? "Someone")}
          </span>
          <time dateTime={reply.createdAt} title={formatDate(reply.createdAt)}>
            {relativeTime(reply.createdAt)}
          </time>
        </p>
        <p className="mt-0.5 text-[16px] leading-[1.6] whitespace-pre-wrap text-[color:var(--color-ink)] [overflow-wrap:anywhere]">
          {reply.body}
        </p>
        <Attachments items={reply.attachments} className="mt-2" />
      </div>
    </li>
  );
}

/**
 * A reply sent from this card, shown before the list carries it.
 * `knownIds` are the stored replies at the moment it went, so its saved
 * copy can be told apart from an older reply with the same words.
 */
interface SentReply {
  reply: Reply;
  knownIds: string[];
}

/**
 * The sent replies the list doesn't carry yet. A stored reply stands in
 * for one sent reply when it is new since that reply went and has the
 * same words and author.
 */
function unsavedReplies(stored: Reply[], sent: SentReply[]): Reply[] {
  const claimed = new Set<string>();
  const waiting: Reply[] = [];
  for (const { reply, knownIds } of sent) {
    const saved = stored.find(
      (r) =>
        !claimed.has(r.id) &&
        !knownIds.includes(r.id) &&
        r.body === reply.body &&
        (r.authorEmail ?? "") === (reply.authorEmail ?? ""),
    );
    if (saved) {
      claimed.add(saved.id);
    } else {
      waiting.push(reply);
    }
  }
  return waiting;
}

/**
 * The replies under an item, oldest first, and a box to add one. A
 * sent reply is shown at once and the list refetched behind it. The
 * list refreshes every 30 seconds, so this is never remounted on it: a
 * half-typed reply and chosen photos survive Claude answering or moving
 * the item meanwhile. A reply that fails to send goes back in the box.
 */
function Thread({ item }: { item: FeedbackItem }) {
  const { user } = useAuth();
  const utils = trpc.useUtils();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [sent, setSent] = useState<SentReply[]>([]);
  const [sendError, setSendError] = useState<string | null>(null);
  const sentCount = useRef(0);
  const replyId = useId();
  const files = usePendingAttachments();
  const reply = trpc.adminReplyFeedback.useMutation({
    onSuccess: () => void utils.adminListFeedback.invalidate(),
  });
  const replies = [...item.replies, ...unsavedReplies(item.replies, sent)];
  const trimmed = draft.trim();
  const upload = files.upload;
  const busy = reply.isPending || upload.kind === "uploading";

  function send() {
    if (trimmed.length === 0 || busy) {
      return;
    }
    setSendError(null);
    void (async () => {
      const attachments = await files.uploadAll();
      if (attachments === null) {
        return;
      }
      const input: ReplyInput = { id: item.id, body: trimmed };
      if (user?.fullName) {
        input.name = user.fullName;
      }
      if (attachments.length > 0) {
        input.attachments = attachments;
      }
      sentCount.current += 1;
      const sentId = `sent-${sentCount.current}`;
      setSent((current) => [
        ...current,
        {
          reply: {
            id: sentId,
            authorKind: "person",
            authorName: user?.fullName ?? null,
            authorEmail: user?.email ?? null,
            body: trimmed,
            attachments,
            createdAt: new Date().toISOString(),
          },
          knownIds: item.replies.map((r) => r.id),
        },
      ]);
      setDraft("");
      setOpen(false);
      reply.mutate(input, {
        // Chosen files stay until the reply is saved, so a failed send
        // can go again with its photos.
        onSuccess: () => files.clear(),
        onError: (err) => {
          setSent((current) => current.filter((s) => s.reply.id !== sentId));
          setDraft(trimmed);
          setOpen(true);
          setSendError(
            friendlyError(
              err,
              "Your reply didn't send. Check your internet connection and try again.",
            ),
          );
        },
      });
    })();
  }

  function cancel() {
    files.clear();
    setDraft("");
    setSendError(null);
    setOpen(false);
  }

  return (
    <div className="mt-5">
      {replies.length > 0 ? (
        <ul className="flex flex-col gap-4 border-l-2 border-[color:var(--color-line)] pl-4">
          {replies.map((r) => (
            <ReplyLine key={r.id} reply={r} />
          ))}
        </ul>
      ) : null}
      {open ? (
        <div className="mt-4">
          <div className="field-label">
            <label htmlFor={replyId}>Your reply</label>
            <textarea
              id={replyId}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setSendError(null);
              }}
              rows={3}
              maxLength={MAX_BODY}
              autoFocus
              placeholder="Answer here. Everyone on the team can see it."
              className={`${FIELD_CLASS} resize-y`}
            />
          </div>
          <AttachmentPicker files={files} busy={busy} className="mt-4" />
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={trimmed.length === 0 || busy}
              onClick={send}
              className={PRIMARY_BUTTON}
            >
              <Send size={16} aria-hidden="true" />
              {upload.kind === "uploading"
                ? `Uploading ${upload.index} of ${upload.total}…`
                : "Send"}
            </button>
            <button
              type="button"
              disabled={upload.kind === "uploading"}
              onClick={cancel}
              className="inline-flex min-h-11 items-center px-3 text-[15px] font-semibold text-[color:var(--color-ink-soft)] underline underline-offset-2 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
          {upload.kind === "error" ? (
            <p role="alert" className={`mt-3 ${ERROR_LINE}`}>
              {upload.message}
            </p>
          ) : sendError ? (
            <p role="alert" className={`mt-3 ${ERROR_LINE}`}>
              {sendError}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={reply.isPending}
            onClick={() => setOpen(true)}
            className={SECONDARY_BUTTON}
          >
            <MessageSquare size={16} aria-hidden="true" />
            Reply
          </button>
          {reply.isPending ? (
            <p
              role="status"
              className="text-[14px] text-[color:var(--color-ink-soft)]"
            >
              Sending…
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

/**
 * Organiser row: move the item along by hand. The card keys this on the
 * item's id and `updatedAt`, so when Claude or anyone else changes the
 * item, the row starts again from the new status and link rather than
 * offering to save the old ones back over it (ledger rule 32). An
 * unsaved choice is dropped at that moment.
 */
function ResolveControls({ item }: { item: FeedbackItem }) {
  const { user } = useAuth();
  const utils = trpc.useUtils();
  const fieldId = useId();
  const [status, setStatus] = useState<Status>(
    STATUSES.find((s) => s === item.status) ?? "new",
  );
  const [liveUrl, setLiveUrl] = useState(item.liveUrl ?? "");
  const update = trpc.adminUpdateFeedback.useMutation({
    onSuccess: () => void utils.adminListFeedback.invalidate(),
  });

  const savedUrl = item.liveUrl ?? "";
  const typedUrl = liveUrl.trim();
  // The update keeps the stored link when none is sent, so an emptied
  // field can't remove it; say so rather than pretend to save.
  const clearingLink = typedUrl === "" && savedUrl !== "";
  const dirty = status !== item.status || typedUrl !== savedUrl;

  return (
    <div className="mt-6 border-t border-[color:var(--color-line)] pt-5">
      <fieldset>
        <legend className="field-label">
          Change the status (organisers only)
        </legend>
        {/* Words go in the thread above, where everyone can read and answer
            them; this row only moves the status and records the link. */}
        <div className="mt-3 grid gap-4 md:grid-cols-[220px_minmax(0,420px)]">
          <div className="field-label">
            <label htmlFor={`${fieldId}-status`}>Status</label>
            <select
              id={`${fieldId}-status`}
              value={status}
              onChange={(e) => {
                const next = STATUSES.find((s) => s === e.target.value);
                if (next) {
                  setStatus(next);
                }
              }}
              className={FIELD_CLASS}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </div>
          <div className="field-label">
            <label htmlFor={`${fieldId}-link`}>
              Link to the change (optional)
            </label>
            <input
              id={`${fieldId}-link`}
              type="url"
              inputMode="url"
              value={liveUrl}
              onChange={(e) => setLiveUrl(e.target.value)}
              maxLength={500}
              aria-describedby={clearingLink ? `${fieldId}-clear` : undefined}
              placeholder="https://"
              className={FIELD_CLASS}
            />
          </div>
        </div>
        {clearingLink ? (
          <p
            id={`${fieldId}-clear`}
            className="mt-2 text-[14px] text-[color:var(--color-ink-soft)]"
          >
            To remove a link, ask Adi.
          </p>
        ) : null}
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
          <button
            type="button"
            disabled={update.isPending || !dirty || clearingLink}
            onClick={() => {
              const input: ResolveInput = { id: item.id, status };
              if (typedUrl) {
                input.liveUrl = typedUrl;
              }
              if (user?.fullName) {
                input.name = user.fullName;
              }
              update.mutate(input);
            }}
            className={PRIMARY_BUTTON}
          >
            {update.isPending ? "Saving…" : "Save"}
          </button>
          {item.notifiedAt ? (
            <p className="text-[14px] text-[color:var(--color-ink-soft)]">
              Emailed {item.authorName ?? "the author"}{" "}
              {relativeTime(item.notifiedAt)}
            </p>
          ) : null}
        </div>
        {update.error ? (
          <p role="alert" className={`mt-3 ${ERROR_LINE}`}>
            {friendlyError(update.error)}
          </p>
        ) : null}
      </fieldset>
    </div>
  );
}
